import React from 'react'
import {
  Avatar,
  CircularProgress,
  makeStyles,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useTranslate } from 'react-admin'
import QueryKeys from '../../../utils/constants/queryKeys'
import getOrgFansChannels from '../../../Apis/Fans/getOrgFansChannels'
import patchFansConfigurations from '../../../Apis/Fans/patchFansConfigurations'
import createCompetitionChannel from '../../../Apis/Fans/createCompetitionChannel'
import useQueryParams from '../../../hooks/useQueryParams'
import LoaderDots from '../../../components/loaders/LoaderDots/LoaderDots'
import fansChannelsPageStyles from './fansChannelsPage.styles'

const useStyles = makeStyles(fansChannelsPageStyles)

const FansChannelsPage = () => {
  const classes = useStyles()
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const { org: orgId } = useQueryParams()

  const queryKey = [QueryKeys.GET_ORG_FANS_CHANNELS, orgId]

  const { data: rows, isLoading } = useQuery(
    queryKey,
    () => getOrgFansChannels(orgId),
    { enabled: Boolean(orgId) },
  )

  const toggleMutation = useMutation(
    ({ channelId, competitionId, isEnabled }) => {
      if (!channelId) {
        return createCompetitionChannel({ orgId, competitionId })
      }
      return patchFansConfigurations({ orgId, channelId, channel: { id: channelId, isEnabled } })
    },
    {
      onMutate: async ({ competitionId, isEnabled }) => {
        await queryClient.cancelQueries(queryKey)
        const previous = queryClient.getQueryData(queryKey)
        queryClient.setQueryData(queryKey, (old) =>
          (old || []).map((row) =>
            row.competitionId === competitionId ? { ...row, isEnabled } : row,
          ),
        )
        return { previous }
      },
      onError: (_err, _vars, context) => {
        if (context?.previous) queryClient.setQueryData(queryKey, context.previous)
      },
      onSettled: () => queryClient.invalidateQueries(queryKey),
    },
  )

  const handleToggle = (row) => {
    toggleMutation.mutate({
      channelId: row.channelId,
      competitionId: row.competitionId,
      isEnabled: !row.isEnabled,
    })
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <LoaderDots />
      </div>
    )
  }

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant='h5'>{translate('ra.text.fansChannels') || 'Fans channels'}</Typography>
        <Typography variant='body2' color='textSecondary'>
          {translate('ra.text.fansChannelsHint') ||
            'Enable or disable the Fans channel for each competition. Changes save automatically.'}
        </Typography>
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{translate('ra.text.competition') || 'Competition'}</TableCell>
              <TableCell align='right'>{translate('ra.text.fansEnabled') || 'Fans enabled'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows && rows.length > 0 ? (
              rows.map((row) => (
                <TableRow key={row.competitionId} hover>
                  <TableCell>
                    <div className={classes.competitionInfo}>
                      {row.competitionLogoUrl && (
                        <Avatar
                          src={row.competitionLogoUrl}
                          alt={row.competitionName}
                          className={classes.competitionLogo}
                        >
                          {row.competitionName?.charAt(0)}
                        </Avatar>
                      )}
                      <Typography variant='body2'>{row.competitionName}</Typography>
                    </div>
                  </TableCell>
                  <TableCell align='right'>
                    {toggleMutation.isLoading &&
                    toggleMutation.variables?.competitionId === row.competitionId ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Switch
                        checked={row.isEnabled}
                        onChange={() => handleToggle(row)}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align='center' className={classes.emptyState}>
                  {translate('ra.text.noCompetitions') ||
                    'No competitions yet. Create a competition to enable a Fans channel for it.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default FansChannelsPage
